import { zImageTypeEnum } from '@/schemas/imageTypeEnumSchema'

import type { ImageSchema } from '@/schemas/imageSchema'

export const ProductImageFormat = {
  gallery: 'GALLERY',
  thumbnail100: 'THUMBNAIL_100',
  thumbnail350: 'THUMBNAIL_350',
} as const

type ProductImageFormat = (typeof ProductImageFormat)[keyof typeof ProductImageFormat]

type ProductImage = Partial<Pick<ImageSchema, 'imageType' | 'format' | 'galleryIndex'>>

const SINGLE_IMAGE_GALLERY_INDEX = 0

const isGalleryImage = (image: ProductImage) => image.imageType === zImageTypeEnum.enum.GALLERY

const groupByGalleryIndex = <T extends ProductImage>(images: readonly T[]): T[][] => {
  const groups = new Map<number, T[]>()

  for (const image of images.filter(isGalleryImage)) {
    const index = image.galleryIndex ?? SINGLE_IMAGE_GALLERY_INDEX
    groups.set(index, [...(groups.get(index) ?? []), image])
  }

  return [...groups.entries()].sort(([indexA], [indexB]) => indexA - indexB).map(([, group]) => group)
}

const FORMATS_FROM_SMALLEST: readonly ProductImageFormat[] = [
  ProductImageFormat.thumbnail100,
  ProductImageFormat.thumbnail350,
  ProductImageFormat.gallery,
]

const formatAndLarger = (format: ProductImageFormat) =>
  FORMATS_FROM_SMALLEST.slice(FORMATS_FROM_SMALLEST.indexOf(format))

const pickFormat = <T extends ProductImage>(sameImageFormats: readonly T[], format: ProductImageFormat): T => {
  for (const candidate of formatAndLarger(format)) {
    const image = sameImageFormats.find(({ format }) => format === candidate)
    if (image) {
      return image
    }
  }

  return sameImageFormats[0]
}

export const getPrimaryImage = <T extends ProductImage>(images: readonly T[] = []): T | undefined =>
  images.find((image) => image.imageType === zImageTypeEnum.enum.PRIMARY) ?? images[0]

export const getGalleryImages = <T extends ProductImage>(
  images: readonly T[] = [],
  format: ProductImageFormat
): T[] => {
  const galleryImages = groupByGalleryIndex(images).map((sameImageFormats) => pickFormat(sameImageFormats, format))
  if (galleryImages.length > 0) {
    return galleryImages
  }

  const primaryImage = getPrimaryImage(images)
  return primaryImage ? [primaryImage] : []
}

export const getProductThumbnail = <T extends ProductImage>(images: readonly T[] = []): T | undefined =>
  getGalleryImages(images, ProductImageFormat.thumbnail350)[0]
